
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Play, AlertTriangle, Download, Key, Plug, RefreshCw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { PluginBackupMonitor } from "./PluginBackupMonitor";

export const PluginTestingDashboard = () => {
  const [activeBackupId, setActiveBackupId] = useState<string | undefined>();
  const [isTestingAll, setIsTestingAll] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});

  const runTest = async (testName: string) => {
    setTestResults(prev => ({ ...prev, [testName]: undefined }));
    
    // Simulate test running
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Simulate test success (in a real app, this would actually test the functionality)
    const success = Math.random() > 0.2; // 80% success rate for demo purposes
    
    setTestResults(prev => ({ ...prev, [testName]: success }));
    
    if (success) {
      toast({
        title: "Test Passed",
        description: `${testName} test was successful`
      });
    } else {
      toast({
        title: "Test Failed",
        description: `${testName} test encountered an issue`,
        variant: "destructive"
      });
    }
    
    return success;
  };
  
  const runAllTests = async () => {
    setIsTestingAll(true);
    setTestResults({});
    
    const tests = ["downloadTest", "apiKeyTest", "registrationTest", "connectionTest"];
    let allPassed = true;
    
    for (const test of tests) {
      const success = await runTest(test);
      if (!success) allPassed = false;
    }
    
    // If all tests pass, trigger a backup simulation
    if (allPassed) {
      toast({
        title: "All Tests Passed",
        description: "Starting a test backup..."
      });
      
      // Generate a backup ID
      const newBackupId = `test_backup_${Date.now()}`;
      setActiveBackupId(newBackupId);
    }
    
    setIsTestingAll(false);
  };
  
  const startTestBackup = () => {
    const newBackupId = `test_backup_${Date.now()}`;
    setActiveBackupId(newBackupId);
    
    toast({
      title: "Test Backup Started",
      description: "Initiated a test backup"
    });
  };
  
  const TestResultIcon = ({ result }: { result: boolean | undefined }) => {
    if (result === undefined) return <RefreshCw className="h-4 w-4 animate-spin text-yellow-500" />;
    return result ? 
      <Check className="h-4 w-4 text-green-500" /> :
      <AlertTriangle className="h-4 w-4 text-red-500" />;
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Plugin Feature Tests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <Download className="h-5 w-5 mr-2 text-blue-500" />
                    <h3 className="font-medium">Download Test</h3>
                  </div>
                  {testResults.downloadTest !== undefined && (
                    <TestResultIcon result={testResults.downloadTest} />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Verifies plugin file download functionality
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => runTest("downloadTest")}
                  disabled={isTestingAll}
                >
                  Run Test
                </Button>
              </div>
              
              <div className="p-4 border rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <Key className="h-5 w-5 mr-2 text-amber-500" />
                    <h3 className="font-medium">API Key Test</h3>
                  </div>
                  {testResults.apiKeyTest !== undefined && (
                    <TestResultIcon result={testResults.apiKeyTest} />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Tests API key generation and validation
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => runTest("apiKeyTest")}
                  disabled={isTestingAll}
                >
                  Run Test
                </Button>
              </div>
              
              <div className="p-4 border rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <Plug className="h-5 w-5 mr-2 text-purple-500" />
                    <h3 className="font-medium">Registration Test</h3>
                  </div>
                  {testResults.registrationTest !== undefined && (
                    <TestResultIcon result={testResults.registrationTest} />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Verifies plugin registration process
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => runTest("registrationTest")}
                  disabled={isTestingAll}
                >
                  Run Test
                </Button>
              </div>
              
              <div className="p-4 border rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <Plug className="h-5 w-5 mr-2 text-green-500" />
                    <h3 className="font-medium">Connection Test</h3>
                  </div>
                  {testResults.connectionTest !== undefined && (
                    <TestResultIcon result={testResults.connectionTest} />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Tests WordPress site connection
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => runTest("connectionTest")}
                  disabled={isTestingAll}
                >
                  Run Test
                </Button>
              </div>
            </div>
            
            <div className="flex justify-between mt-4">
              <Button 
                variant="default" 
                onClick={runAllTests}
                disabled={isTestingAll}
              >
                {isTestingAll ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Running Tests...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run All Tests
                  </>
                )}
              </Button>
              
              <Button
                variant="outline"
                onClick={startTestBackup}
                disabled={isTestingAll}
              >
                Test Backup Process
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <PluginBackupMonitor 
        backupId={activeBackupId} 
      />
    </div>
  );
};
